import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import DatabaseService from '../../services/DatabaseService';
import { ERROR_MESSAGES } from '../../constants';
import { useLanguage } from '../../contexts/LanguageContext';

const ServiceForm = ({ initialData, staffUsers, maxBookingNumber = 2, onClose, onSubmit }) => {
  const { t } = useLanguage();
  
  // Get max booking number from settings, default to 2 if not set
  const { register, formState: { errors } } = useForm();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    max_number: '',
    staff_ids: [],
  });

  const fetchDefaultSettings = useCallback(async () => {
  try {
    const dbService = DatabaseService.getInstance();
    const [duration, price] = await Promise.all([
      dbService.getSettingsByKey('service', 'defaultServiceDuration'),
      dbService.getSettingsByKey('service', 'defaultServicePrice')
    ]);

    setFormData(prev => ({
      ...prev,
      duration: duration ? parseInt(duration) : 60,
      price: price ? parseFloat(price) : 100,
      max_number: maxBookingNumber
    }));
  } catch (error) {
    console.error('Failed to fetch settings:', error);
    toast.error(ERROR_MESSAGES.FAILED_LOAD_DEFAULT_SETTINGS);
  }
}, [maxBookingNumber]);

// Only apply default values when creating new
useEffect(() => {
  if (!initialData) {
    fetchDefaultSettings();
  }
}, [fetchDefaultSettings, initialData]);
  
  // Initialize staff_ids, if in edit mode and has staff_id or staff_ids
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        id: initialData.id,
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price !== undefined && initialData.price !== null ? initialData.price : 0,
        duration: initialData.duration || '',
        max_number: initialData.max_number || initialData.max || '',
        staff_ids: initialData.staff_ids && initialData.staff_ids.length > 0
          ? initialData.staff_ids
          : initialData.staff_id
            ? [initialData.staff_id]
            : []
      }));
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  // Handle staff multi-selection
  const handleStaffChange = (staffId) => {
    setFormData(prev => {
      const currentStaffIds = Array.isArray(prev.staff_ids) ? [...prev.staff_ids] : [];
      
      // If already selected, remove it; otherwise add it
      if (currentStaffIds.includes(staffId)) {
        return {
          ...prev,
          staff_ids: currentStaffIds.filter(id => id !== staffId)
        };
      } else {
        return {
          ...prev,
          staff_ids: [...currentStaffIds, staffId]
        };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate Max Number field
    const maxNumberValue = parseInt(formData.max_number);
    if (!maxNumberValue || maxNumberValue < 1 || maxNumberValue > maxBookingNumber) {
      toast.error(t('messages.error.maxNumberValidation', { maxValue: maxBookingNumber + 1 }));
      return;
    }
    
    // Ensure staff_ids is a valid array
    const staff_ids = formData.staff_ids || [];
    // Prepare submit data, maintain backward compatibility
    const submitData = {
      ...formData,
      max_number: maxNumberValue,
      staff_ids: staff_ids,
      staff_id: staff_ids.length > 0 ? staff_ids[0] : null
    };
    onSubmit(submitData);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('formLabels.serviceName')} <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">{t('formLabels.description')}</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">{t('formLabels.price')}<span className="text-red-500">*</span></label>
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={handleChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">{t('formLabels.duration')} <span className="text-red-500">*</span></label>
        <input
          type="number"
          name="duration"
          value={formData.duration}
          onChange={handleChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          step="1"
          min="1"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">{t('formLabels.max_number')} <span className="text-red-500">*</span></label>
        <input
          type="number"
          name="max_number"
          value={formData.max_number}
          onChange={handleChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          step="1"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('formLabels.assignedStaff')}</label>
        <div className="mt-1 space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3">
          {staffUsers.map((staff) => (
            <div key={staff.id} className="flex items-center">
              <input
                type="checkbox"
                id={`staff-${staff.id}`}
                checked={Array.isArray(formData.staff_ids) && formData.staff_ids.includes(staff.id)}
                onChange={() => handleStaffChange(staff.id)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor={`staff-${staff.id}`} className="ml-2 block text-sm text-gray-900">
                {staff.full_name}
              </label>
            </div>
          ))}
          {staffUsers.length === 0 && (
            <p className="text-sm text-gray-500">{t('services.noStaffAvailable')}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onClose}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          {t('common.save')}
        </button>
      </div>
    </form>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  return (
    prevProps.initialData === nextProps.initialData &&
    prevProps.staffUsers === nextProps.staffUsers
  );
};

export default React.memo(ServiceForm, propsAreEqual);